/**
 * Agent API service.
 * Pure async functions — no React state, no hooks.
 */
import { get, post, patch, del } from '@core/api/client';
import { endpoints } from '@core/api/endpoints';
import { fetchAllPages, normalizePaginationParams } from '@core/api/pagination';
import type { PaginatedResponse, PaginationParams } from '@core/api/types';

// ── Domain types ──────────────────────────────────────────────────────────────

export type AgentModel =
  | 'gpt-4o'
  | 'gpt-4o-mini'
  | 'gpt-4-turbo'
  | 'gpt-3.5-turbo';

export interface AgentModelConfig {
  chatModel?: AgentModel;
  temperature?: number;
  maxOutputTokens?: number;
  utilityModel?: AgentModel;
}

export interface AgentGroundingConfig {
  confidenceThreshold?: number;
  relevanceJudgeEnabled?: boolean;
  faithfulnessCheckEnabled?: boolean;
}

export interface AgentIngestionConfig {
  enrichmentEnabled?: boolean;
  enrichmentBatchSize?: number;
  enrichmentMaxChunks?: number;
  semanticChunkingEnabled?: boolean;
  semanticMinChunkTokens?: number;
  semanticMaxChunkTokens?: number;
  semanticBreakpointPercentile?: number;
  semanticSentenceOverlap?: number;
  semanticMaxSentences?: number;
}

/** Per-agent widget branding/theme (Step 5a/5b). */
export interface WidgetTheme {
  accent?: string;
  accent2?: string;
  mode?: 'light' | 'dark';
  title?: string;
  subtitle?: string;
  launcherText?: string;
  welcomeTitle?: string;
  welcomeSubtitle?: string;
  channels?: 'chat' | 'voice' | 'both';
  logoUrl?: string;
  suggestedPrompts?: string[];
  poweredBy?: boolean;
}

export type AgentOptimizeFor = 'chat' | 'voice' | 'both';

export interface AgentRetrievalConfig {
  /** Chat / voice / both — persisted UI profile selector. */
  optimizeFor?: AgentOptimizeFor;
  topK?: number;
  minSimilarityScore?: number;
  maxContextTokens?: number;
  retrievalHints?: string[];
  hybridSearchEnabled?: boolean;
  queryExpansionEnabled?: boolean;
  maxExpansionQueries?: number;
  parentChildRetrievalEnabled?: boolean;
  rerankEnabled?: boolean;
  closestMatchEnabled?: boolean;
  chunkSize?: number;
  chunkOverlap?: number;
  grounding?: AgentGroundingConfig;
  ingestion?: AgentIngestionConfig;
  /** Per-channel voice overrides when optimizeFor === 'both'. */
  voice?: Record<string, unknown>;
}

// ── Config schema (from GET /agents/config/schema) ──────────────────────────────

export type AgentConfigGroup =
  | 'profile'
  | 'models'
  | 'retrieval'
  | 'grounding'
  | 'ingestion'
  | 'voice'
  | 'phone';

/** Tab metadata; `visible` is evaluated for the current tenant by the API. */
export interface AgentConfigGroupMeta {
  key: AgentConfigGroup;
  label: string;
  visibleWhen?: string;
  visible?: boolean;
}

export interface AgentConfigSelectOption {
  value: string;
  label: string;
  description?: string;
}

export interface AgentConfigField {
  key: string;
  group: AgentConfigGroup;
  label: string;
  description: string;
  type: 'model' | 'number' | 'boolean' | 'string' | 'string[]' | 'select' | 'textarea';
  default: unknown;
  min?: number;
  max?: number;
  step?: number;
  editable: boolean;
  /** Options for `type: 'select'`. */
  options?: AgentConfigSelectOption[];
  /** True when the field can hold a separate voice-channel value. */
  channelOverridable?: boolean;
  /** Suggested value for the voice channel (used by the "Both" profile). */
  voiceDefault?: unknown;
  /** Short guidance shown under the control. */
  note?: string;
  /** Phone tab: the value the phone profile uses when left unset. */
  phoneDefault?: unknown;
  /** Declarative visibility rule, e.g. `capability:inbound_calls`. */
  visibleWhen?: string;
}

export interface AgentModelOption {
  value: AgentModel;
  label: string;
  description: string;
}

export interface AgentOptimizeForOption {
  value: AgentOptimizeFor;
  label: string;
  description: string;
}

/** Dot-path → value bundles the UI applies when an optimize-for target is picked. */
export type AgentTuningProfiles = Record<'voice' | 'chat' | 'thorough', Record<string, unknown>>;

export interface AgentConfigSchema {
  models: AgentModelOption[];
  fields: AgentConfigField[];
  optimizeForOptions?: AgentOptimizeForOption[];
  profiles?: AgentTuningProfiles;
  /** Tab list with per-tenant visibility (older APIs omit it). */
  groups?: AgentConfigGroupMeta[];
}

export interface Agent {
  id: string;
  tenantId: string;
  tenantName?: string;
  name: string;
  slug: string;
  description?: string;
  systemPrompt: string;
  model: AgentModel;
  modelConfig?: AgentModelConfig;
  retrievalConfig?: AgentRetrievalConfig;
  widgetTheme?: WidgetTheme | null;
  isActive: boolean;
  documentCount: number;
  conversationCount: number;
  createdAt?: string;
  updatedAt?: string;
}

/** UI / hook input — `model` is mapped to `modelConfig.chatModel` for the API */
export interface CreateAgentPayload {
  name: string;
  slug: string;
  description?: string;
  systemPrompt: string;
  model: AgentModel;
  modelConfig?: AgentModelConfig;
  retrievalConfig?: AgentRetrievalConfig;
  widgetTheme?: WidgetTheme | null;
  isActive?: boolean;
}

export interface UpdateAgentPayload extends Partial<CreateAgentPayload> {}

export interface UpdateSystemPromptPayload {
  systemPrompt: string;
}

// ── API mapping (backend uses modelConfig, not top-level model) ───────────────

const DEFAULT_MODEL: AgentModel = 'gpt-4o-mini';

type AgentApiRecord = Omit<Agent, 'model' | 'documentCount' | 'conversationCount'> & {
  documentCount?: number;
  conversationCount?: number;
};

function mapAgentFromApi(raw: AgentApiRecord): Agent {
  const chatModel = raw.modelConfig?.chatModel;
  const name = raw.name?.trim() || raw.slug?.trim() || 'Unnamed Agent';

  return {
    ...raw,
    name,
    slug: raw.slug?.trim() ?? '',
    systemPrompt: raw.systemPrompt ?? '',
    tenantName: raw.tenantName?.trim() || undefined,
    documentCount: Number(raw.documentCount ?? 0),
    conversationCount: Number(raw.conversationCount ?? 0),
    model:
      typeof chatModel === 'string' && chatModel
        ? (chatModel as AgentModel)
        : DEFAULT_MODEL,
  };
}

function mapPaginated<T extends AgentApiRecord>(
  res: PaginatedResponse<T>,
): PaginatedResponse<Agent> {
  return { ...res, items: res.items.map(mapAgentFromApi) };
}

function toApiCreateBody(payload: CreateAgentPayload) {
  const { model, modelConfig, ...rest } = payload;
  return {
    ...rest,
    modelConfig: {
      ...modelConfig,
      chatModel: model,
    },
  };
}

function toApiUpdateBody(payload: UpdateAgentPayload) {
  const { model, modelConfig, ...rest } = payload;
  if (model === undefined) {
    return modelConfig ? { ...rest, modelConfig } : rest;
  }
  return {
    ...rest,
    modelConfig: {
      ...modelConfig,
      chatModel: model,
    },
  };
}

// ── Service ───────────────────────────────────────────────────────────────────

export const agentService = {
  list(params?: PaginationParams): Promise<PaginatedResponse<Agent>> {
    return get<PaginatedResponse<AgentApiRecord>>(endpoints.agents.list, {
      params: normalizePaginationParams(params),
    }).then(mapPaginated);
  },

  listByTenant(
    tenantId: string,
    params?: PaginationParams,
  ): Promise<PaginatedResponse<Agent>> {
    return get<PaginatedResponse<AgentApiRecord>>(
      endpoints.agents.listByTenant(tenantId),
      { params: normalizePaginationParams(params) },
    ).then(mapPaginated);
  },

  listAll(params?: PaginationParams): Promise<Agent[]> {
    return fetchAllPages((page, limit) => agentService.list({ page, limit }), params?.limit);
  },

  listAllByTenant(tenantId: string, params?: PaginationParams): Promise<Agent[]> {
    return fetchAllPages(
      (page, limit) => agentService.listByTenant(tenantId, { page, limit }),
      params?.limit,
    );
  },

  getById(agentId: string): Promise<Agent> {
    return get<AgentApiRecord>(endpoints.agents.getById(agentId)).then(
      mapAgentFromApi,
    );
  },

  /** Field catalog + defaults + descriptions for the settings UI. */
  getConfigSchema(): Promise<AgentConfigSchema> {
    return get<AgentConfigSchema>(endpoints.agents.configSchema);
  },

  create(payload: CreateAgentPayload): Promise<Agent> {
    return post<AgentApiRecord>(
      endpoints.agents.create,
      toApiCreateBody(payload),
    ).then(mapAgentFromApi);
  },

  update(agentId: string, payload: UpdateAgentPayload): Promise<Agent> {
    return patch<AgentApiRecord>(
      endpoints.agents.update(agentId),
      toApiUpdateBody(payload),
    ).then(mapAgentFromApi);
  },

  updateSystemPrompt(
    agentId: string,
    payload: UpdateSystemPromptPayload,
  ): Promise<Agent> {
    return patch<AgentApiRecord>(endpoints.agents.update(agentId), payload).then(
      mapAgentFromApi,
    );
  },

  delete(agentId: string): Promise<void> {
    return del(endpoints.agents.delete(agentId));
  },

  activate(agentId: string): Promise<Agent> {
    return post<AgentApiRecord>(endpoints.agents.activate(agentId)).then(
      mapAgentFromApi,
    );
  },

  deactivate(agentId: string): Promise<Agent> {
    return post<AgentApiRecord>(endpoints.agents.deactivate(agentId)).then(
      mapAgentFromApi,
    );
  },
} as const;
